import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Brain, Mail } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, Input } from '../../components/index.js';
import { useAuth } from '../../lib/auth-context.js';

export function Login() {
  const { signIn, signInWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setLoading(false);
    }
  };

  const onForgotPassword = async () => {
    const target = email.trim();
    if (!target) {
      setError('Enter your email above, then tap “Forgot password?”.');
      return;
    }
    setError(null);
    setResetting(true);
    try {
      await resetPassword(target);
      // Firebase returns success even for unknown emails (no account enumeration).
      // Phrase the confirmation to match: "if an account exists…".
      setResetSentTo(target);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setResetting(false);
    }
  };

  const onGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      navigate(from, { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <header className="mb-8 text-center">
          <span className="inline-flex size-12 items-center justify-center rounded-xl bg-accent-muted text-accent-hover mb-4">
            <Brain className="size-6" aria-hidden />
          </span>
          <h1 className="text-3xl font-bold text-text-primary">Sign in to TAS Portal</h1>
          <p className="mt-2 text-base text-text-secondary">Welcome back.</p>
        </header>
        <Card>
          <CardHeader title="Email &amp; password" />
          <CardBody>
            <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.currentTarget.value);
                  // Drop the stale "reset sent to <old email>" banner once the
                  // address changes, so it can't point at the wrong inbox.
                  if (resetSentTo) setResetSentTo(null);
                }}
                leftAdornment={<Mail className="size-4" aria-hidden />}
              />
              <div className="flex flex-col gap-1.5">
                <Input
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                />
                <button
                  type="button"
                  onClick={onForgotPassword}
                  disabled={resetting}
                  className="self-end text-sm text-accent-hover hover:underline disabled:opacity-50"
                >
                  {resetting ? 'Sending…' : 'Forgot password?'}
                </button>
              </div>
              {resetSentTo && (
                <p role="status" className="text-sm text-success">
                  If an account exists for {resetSentTo}, a password-reset link is on its way.
                  Check your inbox (and spam).
                </p>
              )}
              {error && (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              )}
              <Button type="submit" loading={loading} fullWidth>
                Sign in
              </Button>
            </form>
            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-border-subtle" aria-hidden />
              <span className="text-sm text-text-secondary">or</span>
              <span className="h-px flex-1 bg-border-subtle" aria-hidden />
            </div>
            <Button type="button" variant="secondary" fullWidth onClick={onGoogle} loading={googleLoading}>
              Continue with Google
            </Button>
          </CardBody>
        </Card>
        <p className="mt-6 text-center text-sm text-text-secondary">
          New here?{' '}
          <Link to="/signup" className="text-accent-hover hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </main>
  );
}

function messageFor(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: string }).code;
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Email or password is incorrect.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Try again in a minute.';
      case 'auth/missing-email':
      case 'auth/invalid-email':
        return 'That email address is not valid.';
      case 'auth/network-request-failed':
        return 'Network error. Check your connection and try again.';
      case 'auth/popup-closed-by-user':
        return 'Google sign-in was cancelled.';
      default:
        return code.replace(/^auth\//, '').replace(/-/g, ' ');
    }
  }
  return err instanceof Error ? err.message : 'Sign-in failed.';
}
